/**
 * Programmatic industry pages: /solutions (index) and /solutions/:slug (detail).
 * Data lives in src/data/industries.ts. Each detail page sets its own SEO meta +
 * FAQPage JSON-LD.
 */
import { BrandMark } from '../components/BrandMark';
import { Link, useParams, Navigate } from 'react-router-dom';
import { industries, getIndustry } from '../data/industries';
import { useSeo } from '../hooks/useSeo';
import { renderInline } from './Blog';

/** Four industries ship a setup script; healthcare does not. Mirrors the same
 *  set in scripts/dev/gen-content-pages.mjs. */
const READY_MADE = new Set(['retail', 'hospitality', 'manufacturing', 'professional-services']);

const SITE = 'https://agent-mesh.org';

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-white text-slate-900">
      <header className="border-b">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-5 py-4">
          <Link to="/" className="flex items-center gap-2">
            <BrandMark className="h-7 w-7" />
            <span className="text-lg font-bold">Agent<span className="text-brand">Mesh</span></span>
          </Link>
          <Link
            to="/register"
            className="rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-700"
          >
            Start free
          </Link>
        </div>
      </header>
      <main className="mx-auto max-w-4xl px-5 py-12">{children}</main>
      <footer className="border-t">
        <div className="mx-auto max-w-4xl px-5 py-8 text-sm text-slate-500">
          {/* /example and /blog also live in the prerendered footer in
              gen-content-pages.mjs — both were orphans. Keep the two in step. */}
          <Link to="/solutions" className="hover:underline">
            All industries
          </Link>{' '}
          · <Link to="/compare" className="hover:underline">Compare</Link> ·{' '}
          <Link to="/example" className="hover:underline">See a real run</Link> ·{' '}
          {/* Plain <a>: prerendered HTML, no React route. */}
          <a href="/agent-library" className="hover:underline">Agents</a> ·{' '}
          <Link to="/blog" className="hover:underline">Writing</Link> ·{' '}
          <Link to="/" className="hover:underline">Home</Link>
        </div>
      </footer>
    </div>
  );
}

export function SolutionsIndex() {
  useSeo({
    title: 'AI Agents by Industry',
    description:
      'How AgentMesh — no-code teams of AI agents that reason, use tools and automate — helps retail, hospitality, professional services, manufacturing and healthcare.',
    canonical: `${SITE}/solutions`,
  });
  return (
    <Shell>
      <h1 className="mb-2 text-3xl font-bold">AI agents for your industry</h1>
      <p className="mb-2 max-w-2xl text-slate-600">
        No-code teams of AI agents that reason, use your tools, and run on their
        own — tailored to how your industry actually works.
      </p>
      <p className="mb-8 text-sm text-slate-500">
        Also:{' '}
        <Link to="/compare" className="text-brand hover:underline">
          Compare AgentMesh
        </Link>{' '}
        ·{' '}
        <Link to="/help" className="text-brand hover:underline">
          Help center
        </Link>
      </p>
      <ul className="grid gap-3 sm:grid-cols-2">
        {industries.map((i) => (
          <li key={i.slug}>
            <Link
              to={`/solutions/${i.slug}`}
              className="block rounded-lg border p-4 hover:border-slate-400"
            >
              <span className="text-lg font-semibold">{i.name}</span>
              <p className="mt-1 text-sm text-slate-600">{i.metaDescription}</p>
            </Link>
          </li>
        ))}
      </ul>
    </Shell>
  );
}

export function SolutionPage() {
  const { slug = '' } = useParams();
  const ind = getIndustry(slug);

  useSeo({
    title: ind ? ind.title : 'AI Agents by Industry',
    description: ind?.metaDescription,
    canonical: ind ? `${SITE}/solutions/${ind.slug}` : `${SITE}/solutions`,
    jsonLd: ind
      ? [
          {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: ind.faqs.map((f) => ({
              '@type': 'Question',
              name: f.q,
              acceptedAnswer: { '@type': 'Answer', text: f.a },
            })),
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Solutions', item: `${SITE}/solutions` },
              { '@type': 'ListItem', position: 2, name: ind.title, item: `${SITE}/solutions/${ind.slug}` },
            ],
          },
        ]
      : undefined,
  });

  if (!ind) return <Navigate to="/solutions" replace />;

  return (
    <Shell>
      <p className="mb-2 text-sm font-medium text-slate-500">{ind.name}</p>
      <h1 className="mb-4 text-3xl font-bold">{ind.title}</h1>
      <div className="mb-8 max-w-2xl rounded-lg border-l-4 border-slate-900 bg-slate-50 p-4">
        <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
          Quick answer
        </p>
        <p className="text-slate-800">{ind.intro}</p>
      </div>

      <h2 className="mb-3 text-xl font-semibold">What slows {ind.name} teams down</h2>
      <ul className="mb-10 list-disc space-y-1 pl-6 text-slate-700">
        {ind.pains.map((p) => (
          <li key={p}>{p}</li>
        ))}
      </ul>

      {/* Everything from here to the FAQ mirrors the industry block in
          scripts/dev/gen-content-pages.mjs, which prerenders this page for
          crawlers. The team was three cards that never said what you had to
          CONNECT, and the page carried no cost and no failure mode at all
          (12 Sept 2026). Keep the two in step. */}
      <h2 className="mb-3 text-xl font-semibold">A starting agent team</h2>
      <div className="mb-4 overflow-x-auto">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b text-left">
              <th className="py-3 pr-4 font-semibold">Agent</th>
              <th className="py-3 pr-4 font-semibold">What it does</th>
              <th className="py-3 font-semibold">Tools to enable</th>
            </tr>
          </thead>
          <tbody>
            {ind.team.map((a) => (
              <tr key={a.name} className="border-b last:border-0 align-top">
                <td className="py-3 pr-4 font-medium">{a.name}</td>
                <td className="py-3 pr-4 text-slate-700">{a.does}</td>
                <td className="py-3 text-slate-600">{renderInline(a.tools)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="mb-10 text-slate-700">
        {READY_MADE.has(ind.slug) ? (
          <>
            This team ships as a ready-made setup for {ind.name} — install it and you have the three
            agents above and the workflows between them, then edit each one's instructions.
          </>
        ) : (
          <>
            <strong>There is no ready-made {ind.name} setup.</strong> Four industries ship one and
            this is not one of them, so the three agents above are built by hand — about two minutes
            each. Saying so here is cheaper than you finding out after signing up.
          </>
        )}
      </p>

      <h2 className="mb-3 text-xl font-semibold">What a run like this costs</h2>
      <p className="mb-3 text-slate-700">
        Runs are billed in tokens, so the price follows the size of the job.{' '}
        <strong>Every figure below is measured on this product</strong>, and all three are the same
        shape of work as the agents above — one agent reading something, deciding, and calling a tool:
      </p>
      <ul className="mb-3 list-disc space-y-1 pl-6 text-slate-700">
        <li>A support ticket triaged and answered (worked example) — <strong>8.4 seconds, $0.0144</strong></li>
        <li>The same job, escalated to a human as well — <strong>28.5 seconds, $0.0204</strong></li>
        <li>One that failed on its first tool call — <strong>4.1 seconds, $0.0024</strong></li>
      </ul>
      <p className="mb-10 text-slate-700">
        <strong>Those are measured on a support workload, not a {ind.name.toLowerCase()} one</strong>,
        so treat them as the order of magnitude rather than your number: cents per job, not dollars.
        Source: <Link to="/example" className="underline">three runs opened up step by step</Link>,
        readable without an account. The free plan gives you 100 runs or $0.50 of usage a day,
        whichever comes first — enough to measure your own.
      </p>

      <h2 className="mb-3 text-xl font-semibold">
        Where this goes wrong in {ind.name.toLowerCase()}
      </h2>
      <h3 className="mb-1 mt-4 font-medium">It answers from data it never read</h3>
      <p className="mb-3 text-slate-700">
        The failure to plan for. Source:{' '}
        <a href="/blog-assets/run-fff256c0.json" className="underline">a logged run of mine</a>{' '}
        finished <code>COMPLETED</code> after two lookups returned HTTP 403 with{' '}
        <code>"ok": true</code> in the tool result — so the agent believed it had the data, and told
        the customer it had checked. <strong>Read the tool result's status, not the run's.</strong>
      </p>
      <h3 className="mb-1 mt-4 font-medium">It runs for weeks and does nothing</h3>
      <p className="mb-3 text-slate-700">
        A condition that can never match skips its branch and the run still reports success. Have the
        run report <strong>how many items it processed</strong> and compare that against what the
        source handed over. A count of zero on a normally busy morning is a signal; a green status is
        not.
      </p>
      <h3 className="mb-1 mt-4 font-medium">Nobody looks at it</h3>
      <p className="mb-10 text-slate-700">
        The honest rule: <strong>if nobody would notice the agent being wrong for a month, do not
        automate that job yet.</strong> Start with the one where somebody notices the same day, and
        keep a person on anything that sends, pays or cancels.
      </p>

      <h2 className="mb-4 text-xl font-semibold">Frequently asked questions</h2>
      <div className="mb-10 space-y-4">
        {ind.faqs.map((f) => (
          <div key={f.q}>
            <h3 className="font-medium">{f.q}</h3>
            <p className="mt-1 text-slate-600">{f.a}</p>
          </div>
        ))}
      </div>

      <div className="rounded-lg border bg-slate-50 p-6 text-center">
        <p className="mb-3 text-lg font-semibold">
          Build your {ind.name} agent team — free to start
        </p>
        <Link
          to="/register"
          className="inline-block rounded-md bg-slate-900 px-5 py-2.5 font-medium text-white hover:bg-slate-700"
        >
          Start free — no card required
        </Link>
      </div>

      <div className="mt-10 border-t pt-6">
        <h2 className="mb-3 text-lg font-semibold">Keep exploring</h2>
        <ul className="grid gap-2 text-sm sm:grid-cols-2">
          {industries
            .filter((x) => x.slug !== ind.slug)
            .slice(0, 4)
            .map((x) => (
              <li key={x.slug}>
                <Link
                  to={`/solutions/${x.slug}`}
                  className="text-brand hover:underline"
                >
                  AI agents for {x.name}
                </Link>
              </li>
            ))}
          <li>
            <Link to="/compare" className="text-brand hover:underline">
              Compare AgentMesh
            </Link>
          </li>
          <li>
            <Link
              to="/help/ai-agents-for-small-business"
              className="text-brand hover:underline"
            >
              What AI agents can do for a small business
            </Link>
          </li>
        </ul>
      </div>
    </Shell>
  );
}
